/**
 * Memory import — public surface.
 *
 * `parseMemoryImport` is the one entry the wizard calls: strips excluded
 * files, auto-detects the real format (re-labeling a mispicked tile instead
 * of failing), dispatches the platform parser, merges pasted text, dedupes,
 * and enforces the item budget keeping the newest.
 */
import { detectPlatform } from './detect.js';
import { parseChatgpt } from './parsers/chatgpt.js';
import { parseClaude } from './parsers/claude.js';
import { parseCodex } from './parsers/codex.js';
import { parseCursor } from './parsers/cursor.js';
import { parseGemini } from './parsers/gemini.js';
import { parseGenericFiles, parsePasted } from './parsers/generic.js';
import { parseHermes } from './parsers/hermes.js';
import { parseOpenclaw } from './parsers/openclaw.js';
import {
  IMPORT_MAX_ITEMS,
  type MemoryImportItem,
  type MemoryImportParseResult,
  type MemoryImportPlatform,
  type NamedText,
} from './types.js';

export * from './types.js';
export { chunkMarkdown, chunkPaste } from './chunk.js';
export { detectPlatform } from './detect.js';
export { epistemicForHeading, parseGenericFiles, parseGenericJson, parsePasted } from './parsers/generic.js';
export { parseChatgpt } from './parsers/chatgpt.js';
export { parseClaude } from './parsers/claude.js';
export { parseCodex } from './parsers/codex.js';
export { parseCursor } from './parsers/cursor.js';
export { parseGemini } from './parsers/gemini.js';
export { parseHermes } from './parsers/hermes.js';
export { parseOpenclaw } from './parsers/openclaw.js';
export { extractMemoryCandidates } from './zip.js';

/** Never parsed, never uploaded: whole-conversation files and binaries. */
const EXCLUDED_NAMES = new Set(['conversations.json', 'chat.html', 'shared_conversations.json', 'message_feedback.json', 'model_comparisons.json']);
const EXCLUDED_EXTENSIONS = /\.(png|jpe?g|gif|webp|svg|pdf|docx?|xlsx?|pptx?|zip|sqlite|db|bin|mp[34]|wav|heic)$/i;

const PARSERS: Record<Exclude<MemoryImportPlatform, 'other'>, (files: NamedText[]) => MemoryImportItem[]> = {
  chatgpt: parseChatgpt,
  claude: parseClaude,
  codex: parseCodex,
  gemini: parseGemini,
  cursor: parseCursor,
  openclaw: parseOpenclaw,
  hermes: parseHermes,
};

export function excludeNonMemoryFiles(files: NamedText[]): NamedText[] {
  return files.filter((f) => {
    const base = f.name.split('/').pop()?.toLowerCase() ?? f.name.toLowerCase();
    return !EXCLUDED_NAMES.has(base) && !EXCLUDED_EXTENSIONS.test(base);
  });
}

function normalizedKey(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

export function parseMemoryImport(input: {
  platform: MemoryImportPlatform;
  files?: NamedText[];
  pasted?: string;
}): MemoryImportParseResult {
  const warnings: string[] = [];
  const files = excludeNonMemoryFiles(input.files ?? []);

  const detected = files.length > 0 ? detectPlatform(files) : null;
  let platform = input.platform;
  if (detected && detected !== input.platform) {
    platform = detected;
    if (input.platform !== 'other') {
      warnings.push(`These files look like a ${detected} export, so they were read that way.`);
    }
  }

  const parser = platform !== 'other' ? PARSERS[platform] : parseGenericFiles;
  let items = files.length > 0 ? parser(files) : [];
  if (files.length > 0 && items.length === 0) {
    // A known-platform parser found nothing memory-bearing — fall back to reading everything generically.
    items = parseGenericFiles(files);
    if (items.length > 0) warnings.push('Read the files as plain notes.');
  }
  if (input.pasted && input.pasted.trim().length > 0) {
    items = items.concat(parsePasted(input.pasted));
  }

  const seen = new Set<string>();
  const deduped: MemoryImportItem[] = [];
  for (const item of items) {
    const key = normalizedKey(item.text);
    if (key.length === 0 || seen.has(key)) continue;
    seen.add(key);
    deduped.push(item);
  }

  let kept = deduped;
  if (deduped.length > IMPORT_MAX_ITEMS) {
    const dated = deduped.filter((i) => i.observedAt).sort((a, b) => (b.observedAt! < a.observedAt! ? -1 : 1));
    const undated = deduped.filter((i) => !i.observedAt);
    kept = [...undated, ...dated].slice(0, IMPORT_MAX_ITEMS);
    warnings.push(`That's a lot of memory — kept the newest ${IMPORT_MAX_ITEMS}.`);
  }

  return { platform, detected: detected !== null, items: kept, warnings };
}
